import { observer } from "mobx-react-lite";
import { ReactElement, useEffect } from "react";
import { useSegmentedButtonsStore } from "..";
import styles from './SegmentedButtons.module.css';

function SegmentedButtons<T>({ value, onChange, options, isTabSwitchDisabled, className }: ISegmentedButtonsProps<T>) {
    useSegmentedButtonsOverride(value, onChange, options, isTabSwitchDisabled);

    return (
        <div className={[styles.container, className].join(" ")}>
            {options.map(option => (
                <button
                    key={option.value as string}
                    className={[styles.button, value === option.value ? styles.buttonActive : null].join(" ")}
                    onClick={() => onChange(option.value)}
                >{option.label}</button>
            ))}
        </div>
    );
}

export default observer(SegmentedButtons);

export function useSegmentedButtonsOverride<T>(value: T, onChange: (value: T) => void, options: ISegmentedButtonsOption<T>[], isTabSwitchDisabled?: boolean) {
    const segmentedButtonsStore = useSegmentedButtonsStore();

    useEffect(() => {
        const prevButtons = segmentedButtonsStore.buttons;
        const prevIsTabSwitchDisabled = segmentedButtonsStore.isTabSwitchDisabled;
        segmentedButtonsStore.setButtons({
            value: value,
            values: options.map(o => o.value),
            onChange: onChange,
        });

        segmentedButtonsStore.setIsTabSwitchDisabled(isTabSwitchDisabled ?? false);

        return () => {
            segmentedButtonsStore.setButtons(prevButtons);
            segmentedButtonsStore.setIsTabSwitchDisabled(prevIsTabSwitchDisabled);
        }
    }, [value, options, onChange]);
}

interface ISegmentedButtonsProps<T> {
    value: T;
    onChange: (value: T) => void;
    options: ISegmentedButtonsOption<T>[];
    isTabSwitchDisabled?: boolean;
    className?: string;
}

interface ISegmentedButtonsOption<T> {
    value: T;
    label: ReactElement | string;
}